"use client";

import { useMemo, useSyncExternalStore } from "react";
import {
  findActiveTuitionCalculationCycleId,
  readTuitionWorkflowSessionRaw,
  type TuitionWorkflowSession,
} from "@/features/tuition/utils/tuition-workflow-session";

function subscribeToTuitionWorkflowSession(onStoreChange: () => void) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleChange = () => onStoreChange();
  window.addEventListener("tuition-workflow-session-change", handleChange);
  window.addEventListener("storage", handleChange);

  return () => {
    window.removeEventListener("tuition-workflow-session-change", handleChange);
    window.removeEventListener("storage", handleChange);
  };
}

function parseSessionSnapshot(
  raw: string | null,
): TuitionWorkflowSession | null {
  if (!raw) return null;

  try {
    return JSON.parse(raw) as TuitionWorkflowSession;
  } catch {
    return null;
  }
}

export function useTuitionWorkflowSession(cycleId: number | null) {
  const sessionRaw = useSyncExternalStore(
    subscribeToTuitionWorkflowSession,
    () => (cycleId == null ? null : readTuitionWorkflowSessionRaw(cycleId)),
    () => null,
  );

  return useMemo(() => parseSessionSnapshot(sessionRaw), [sessionRaw]);
}

export function useActiveTuitionCalculationCycleId(): number | null {
  const snapshot = useSyncExternalStore(
    subscribeToTuitionWorkflowSession,
    () => {
      const cycleId = findActiveTuitionCalculationCycleId();
      return cycleId == null ? "" : String(cycleId);
    },
    () => "",
  );

  return snapshot === "" ? null : Number(snapshot);
}
